import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Copy, KeyRound } from "lucide-react";
import { useBrowserWallet, useBrowserWalletStore, useWalletContext } from "@nullaone/wallet-react";
import { isPRFSupported, MAX_LOCAL_WALLETS } from "@nullaone/wallet-core";
import { VNextButton } from "@/components/brand/vnext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from "@/i18n";
import { WalletCreationView, WalletCreationProgress, type CreationAvailability } from "./WalletCreationView";

interface VerifyWord { index: number; word: string }
interface CreationState {
  name: string;
  mnemonic: string | null;
  step: "name" | "step1" | "step2" | "mnemonic" | "verify";
  verifyWords: VerifyWord[];
  userInputs: string[];
}

function generateVerifyWords(mnemonic: string): VerifyWord[] {
  const words = mnemonic.trim().split(/\s+/);
  const indices = new Set<number>();
  while (indices.size < Math.min(3, words.length)) indices.add(Math.floor(Math.random() * words.length));
  return [...indices].sort((a, b) => a - b).map(index => ({ index, word: words[index]! }));
}

export function CreateWalletForm() {
  const { t } = useTranslation();
  const { createWallet, connecting, closeModal } = useBrowserWallet();
  const setModalStep = useBrowserWalletStore(store => store.setModalStep);
  const { core } = useWalletContext();
  const [availability, setAvailability] = useState<CreationAvailability>("checking");
  const [checkAttempt, setCheckAttempt] = useState(0);
  const [error, setError] = useState(false);
  const [state, setState] = useState<CreationState>({ name: "", mnemonic: null, step: "name", verifyWords: [], userInputs: [] });
  const inFlight = useRef(false);
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    let current = true;
    // Availability is a preflight, not proof that a credential supports PRF.
    void Promise.all([isPRFSupported(), core.storage.getWalletCountByType()]).then(([supported, counts]) => {
      if (current) setAvailability(!supported ? "unsupported" : counts.local >= MAX_LOCAL_WALLETS ? "limit" : "ready");
    }).catch(() => { if (current) setAvailability("failed"); });
    return () => { current = false; };
  }, [core, checkAttempt]);

  const handleCreate = async () => {
    if (inFlight.current || connecting || availability !== "ready") return;
    inFlight.current = true;
    setError(false);
    setState(previous => ({ ...previous, step: "step1" }));
    try {
      const result = await createWallet(state.name.trim() || undefined, step => {
        if (mounted.current) setState(previous => ({ ...previous, step: step === "encrypting" ? "step2" : "step1" }));
      });
      if (mounted.current) setState(previous => ({ ...previous, mnemonic: result.mnemonic, step: "mnemonic" }));
    } catch {
      if (mounted.current) {
        setError(true);
        setState(previous => ({ ...previous, step: "name" }));
      }
    } finally { inFlight.current = false; }
  };

  if (state.step === "step1" || state.step === "step2") return <WalletCreationProgress step={state.step} />;
  if (state.step === "mnemonic" && state.mnemonic) return <MnemonicStep mnemonic={state.mnemonic} onContinue={() => {
    if (!state.mnemonic) return;
    const verifyWords = generateVerifyWords(state.mnemonic);
    setState(previous => ({ ...previous, step: "verify", verifyWords, userInputs: verifyWords.map(() => "") }));
  }} />;
  if (state.step === "verify" && state.mnemonic) return <VerifyStep verifyWords={state.verifyWords} userInputs={state.userInputs}
    setUserInputs={userInputs => setState(previous => ({ ...previous, userInputs }))}
    onBack={() => setState(previous => ({ ...previous, step: "mnemonic" }))}
    // This only closes the local comparison flow. It does not assert SIWS or persisted backup.
    onVerified={closeModal} />;
  return <WalletCreationView name={state.name} onName={name => setState(previous => ({ ...previous, name }))}
    onCreate={handleCreate} onBack={() => setModalStep("select")} availability={availability} busy={connecting}
    onRetry={() => { setAvailability("checking"); setCheckAttempt(attempt => attempt + 1); }} error={error ? t("welcome.createFailed") : undefined} />;
}

function MnemonicStep({ mnemonic, onContinue }: { mnemonic: string; onContinue: () => void }) {
  const { t } = useTranslation();
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(mnemonic); if (mounted.current) setCopyState("copied"); }
    catch { if (mounted.current) setCopyState("failed"); }
  };
  return <div className="dt-wallet-recovery">
    <div><span className="dt-wallet-symbol"><KeyRound aria-hidden="true" /></span><h2>{t("welcome.backupTitle")}</h2></div>
    <div className="dt-wallet-recovery-warning">{t("welcome.recoveryWarning")}</div>
    <div className="dt-wallet-words">{mnemonic.trim().split(/\s+/).map((word, index) => <div key={index}><small>{index + 1}</small><code>{word}</code></div>)}</div>
    <VNextButton variant="quiet" onClick={handleCopy}><Copy aria-hidden="true" />{t(copyState === "copied" ? "welcome.recoveryCopied" : "welcome.recoveryCopy")}</VNextButton>
    {copyState === "failed" && <p role="alert" className="dt-wallet-error">{t("welcome.copiedFailure")}</p>}
    <VNextButton className="dt-welcome-primary" onClick={onContinue}>{t("welcome.recoveryContinue")}</VNextButton>
  </div>;
}

interface VerifyStepProps {
  verifyWords: VerifyWord[];
  userInputs: string[];
  setUserInputs: (inputs: string[]) => void;
  onVerified: () => void;
  onBack: () => void;
}
function VerifyStep({ verifyWords, userInputs, setUserInputs, onVerified, onBack }: VerifyStepProps) {
  const { t } = useTranslation();
  const isValid = verifyWords.length > 0 && verifyWords.every((word, index) => userInputs[index]?.toLowerCase().trim() === word.word.toLowerCase().trim());
  return <div className="dt-wallet-recovery">
    <h2>{t("welcome.verifyTitle")}</h2><p>{t("welcome.verifyDescription")}</p>
    {verifyWords.map((item, index) => <div className="dt-wallet-field" key={item.index}>
      <Label htmlFor={`word-${item.index}`}>{t("welcome.word", { number: item.index + 1 })}</Label>
      <Input id={`word-${item.index}`} value={userInputs[index] ?? ""} onChange={event => setUserInputs(userInputs.map((value, i) => i === index ? event.target.value : value))} autoComplete="off" autoCapitalize="off" spellCheck={false} />
    </div>)}
    <div className="dt-wallet-recovery-actions"><VNextButton variant="quiet" onClick={onBack}><ArrowLeft aria-hidden="true" />{t("common.back")}</VNextButton><VNextButton disabled={!isValid} onClick={() => { if (isValid) onVerified(); }}>{t("welcome.verifyComplete")}</VNextButton></div>
  </div>;
}
